// ============================================================================
// SttTabViewer.jsx
// Standalone component — place in:
//   ClientApp/src/components/SttTabViewer.jsx
//
// IMPORT in ChatPanel.jsx (add at top of file):
//   import SttTabViewer from "./components/SttTabViewer";
//
// REMOVE from ChatPanel.jsx:
//   Delete the entire existing "const SttTabViewer = ..." block
//
// USAGE in your STT modal — same as before, add 2 new props:
//   <SttTabViewer
//       meetingSttResult={meetingSttResult}
//       isDark={isDark}
//       textColor={textColor}
//       textSecondary={textSecondary}
//       textTertiary={textTertiary}
//       primaryIconColor={primaryIconColor}
//       meetingTitle={meetingContext?.meeting?.displayName ?? "Teams Meeting"}
//       participants={[]}
//   />
//
// TABS:
//   🗣️ Speakers   — utterances with speaker labels + timestamps
//   📝 Words      — word-level detail grid
//   📄 Transcript — full plain text
//   💡 Insights   — auto-generated: Key Points, Takeaways, Action Items,
//                   Decisions, Open Questions, Sentiment Analysis
//   ⚡ Actions    — Email draft, Calendar invite, Post to Teams chat,
//                   Export to Word, Upload to OneDrive
// ============================================================================

import React from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import UsageLogService from "../services/UsageLogService";

//        REPLACE the entire component with this version.
//
// NEW TABS ADDED:
//   💡 Insights — calls /api/sparkinsights/generate → Key Points, Takeaways,
//                 Action Items, Decisions, Open Questions
//   ⚡ Actions  — Send email, Create calendar invite, Post to Teams chat
//
// PROPS: Same as before + add these to the SttTabViewer call in your modal:
//   meetingTitle={meetingContext?.meeting?.displayName ?? "Teams Meeting"}
//   participants={participants}   // array of participant display names if available
// ============================================================================

const SttTabViewer = ({
    meetingSttResult,
    isDark,
    textColor,
    textSecondary,
    textTertiary,
    primaryIconColor,
    meetingTitle,
    participants,
    chatId,            // pass chatId from ChatPanel state — used to post to meeting chat
    teamsParticipants, // array of { displayName, email } from microsoftTeams.meeting.getParticipants()
}) => {
    const [activeTab, setActiveTab] = React.useState("utterances");

    // ── Insights state ────────────────────────────────────────────────────────
    const [insights, setInsights] = React.useState(null);
    const [insightsLoading, setInsightsLoading] = React.useState(false);
    const [insightsError, setInsightsError] = React.useState("");
    const [insightsFetched, setInsightsFetched] = React.useState(false);

    // ── Actions state ─────────────────────────────────────────────────────────
    const [emailDraft, setEmailDraft] = React.useState(null);
    const [emailLoading, setEmailLoading] = React.useState(false);
    const [emailDraftVisible, setEmailDraftVisible] = React.useState(false);
    const [calendarLoading, setCalendarLoading] = React.useState(false);
    const [calendarDone, setCalendarDone] = React.useState(false);
    const [teamsPostLoading, setTeamsPostLoading] = React.useState(false);
    const [teamsPostDone, setTeamsPostDone] = React.useState(false);
    const [wordExportLoading, setWordExportLoading] = React.useState(false);
    const [wordExportDone, setWordExportDone] = React.useState(false);
    const [oneDriveLoading, setOneDriveLoading] = React.useState(false);
    const [oneDriveDone, setOneDriveDone] = React.useState(false);
    const [actionError, setActionError] = React.useState("");

    // ── Colors ────────────────────────────────────────────────────────────────
    const cardBg = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)";
    const cardBorder = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
    const tagBg = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)";
    const successGreen = "#2e7d32";
    const warningAmber = "#e65100";

    // ── Fetch insights when Insights OR Actions tab clicked ─────────────────
    React.useEffect(() => {
        if (activeTab !== "insights" && activeTab !== "actions") return;
        if (insightsFetched || insightsLoading) return;
        if (!meetingSttResult?.fullTranscript) return;
        generateInsights();
    }, [activeTab]);

    // Track page view for analytics when tab changes
    React.useEffect(() => {
        if (meetingSttResult) {
            UsageLogService.trackPageView('SttTabViewer', {
                viewType: 'TranscriptionModal',
                activeTab: activeTab,
                hasTranscript: !!meetingSttResult.fullTranscript,
                utteranceCount: meetingSttResult.utterances?.length || 0,
                wordCount: meetingSttResult.words?.length || 0,
                meetingTitle: meetingTitle || 'Unknown',
                participantCount: participants?.length || 0
            });
        }
    }, [activeTab, meetingSttResult]);

    const generateInsights = async () => {
        setInsightsLoading(true);
        setInsightsError("");
        try {
            const resp = await fetch("/api/sparkinsights/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    transcript: meetingSttResult.fullTranscript,
                    meetingTitle: meetingTitle ?? "Teams Meeting",
                    participants: derivedParticipants.map(p => p.label),
                    utterances: meetingSttResult.utterances?.map(u => ({
                        speakerLabel: u.speakerLabel,
                        text: u.text,
                    })) ?? [],
                }),
            });

            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.error ?? `Server error ${resp.status}`);
            }

            const data = await resp.json();
            setInsights(data);
            setInsightsFetched(true);
        } catch (e) {
            setInsightsError(`Failed to generate insights: ${e.message}`);
        } finally {
            setInsightsLoading(false);
        }
    };

    // ── Generate email draft ──────────────────────────────────────────────────
    // Calls /api/sparkinsights/email-draft-direct — generates email from
    // transcript + participants directly, no insights pre-load required.
    const generateEmailDraft = async () => {
        setEmailLoading(true);
        setActionError("");
        setEmailDraft(null);
        setEmailDraftVisible(false);
        try {
            const participantNames = teamsParticipants?.length
                ? teamsParticipants.map(p => p.displayName || p.email).filter(Boolean)
                : derivedParticipants.map(p => p.label);

            const resp = await fetch("/api/sparkinsights/email-draft-direct", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    transcript: meetingSttResult?.fullTranscript ?? "",
                    meetingTitle: meetingTitle ?? "Teams Meeting",
                    participants: participantNames,
                    insights: insights ?? null,  // pass if already loaded
                }),
            });

            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.error ?? `Server error ${resp.status}`);
            }
            const data = await resp.json();
            if (!data.subject && !data.body) {
                throw new Error("Spark returned an empty draft — please try again.");
            }
            setEmailDraft(data);
            setEmailDraftVisible(true);
        } catch (e) {
            setActionError(`Email draft failed: ${e.message}`);
        } finally {
            setEmailLoading(false);
        }
    };

    // ── Open email client with draft ──────────────────────────────────────────
    const openEmailClient = () => {
        if (!emailDraft) return;
        // Real email addresses from Teams participant list
        const toList = teamsParticipants?.length
            ? teamsParticipants.map(p => p.email || p.userPrincipalName).filter(Boolean)
            : [];
        const to = toList.join(";");
        const sub = encodeURIComponent(emailDraft.subject ?? "");
        // mailto: body has a 2000-char limit — trim gracefully
        const bodyText = (emailDraft.body ?? "").replace(/\n/g, "\r\n");
        const bod = encodeURIComponent(bodyText.slice(0, 1900));
        // mailto: is the correct protocol — Teams desktop routes this to Outlook client
        const mailtoLink = `mailto:${to}?subject=${sub}&body=${bod}`;
        microsoftTeams.app.openLink(mailtoLink).catch(() => {
            window.location.href = mailtoLink;
        });
    };

    // ── Post summary to Teams meeting chat via Graph API ────────────────────
    const postToTeamsChat = async () => {
        if (!meetingSttResult?.fullTranscript) return;
        setTeamsPostLoading(true);
        setActionError("");

        // Build HTML message body — use insights if loaded, else plain transcript
        const lines = [];
        lines.push(`<b>📋 Meeting Summary — ${meetingTitle ?? "Teams Meeting"}</b><br/><br/>`);
        if (insights) {
            if (insights.overallSummary)
                lines.push(`${insights.overallSummary}<br/><br/>`);
            if (insights.keyPoints?.length) {
                lines.push("<b>🔑 Key Points</b><br/>");
                insights.keyPoints.forEach(k => lines.push(`• ${k.point}<br/>`));
                lines.push("<br/>");
            }
            if (insights.actionItems?.length) {
                lines.push("<b>✅ Action Items</b><br/>");
                insights.actionItems.forEach(a => {
                    let item = `• ${a.action}`;
                    if (a.owner) item += ` — <i>${a.owner}</i>`;
                    if (a.deadline) item += ` (${a.deadline})`;
                    lines.push(item + "<br/>");
                });
                lines.push("<br/>");
            }
            if (insights.decisions?.length) {
                lines.push("<b>🎯 Decisions Made</b><br/>");
                insights.decisions.forEach(d => lines.push(`• ${d.decision ?? d.decisionText}<br/>`));
                lines.push("<br/>");
            }
            if (insights.openQuestions?.length) {
                lines.push("<b>❓ Open Questions</b><br/>");
                insights.openQuestions.forEach(q => lines.push(`• ${q.question}<br/>`));
                lines.push("<br/>");
            }
        } else {
            // No insights yet — post the raw transcript
            lines.push("<b>📄 Transcript</b><br/>");
            lines.push(meetingSttResult.fullTranscript.slice(0, 3000).replace(/\n/g, "<br/>"));
            lines.push("<br/><br/>");
        }
        lines.push("<i>— Generated by Spark Anywhere</i>");
        const htmlBody = lines.join("");

        try {
            // Get SSO token to authenticate backend Graph call
            const ssoToken = await microsoftTeams.authentication.getAuthToken().catch(() => "");

            if (chatId && ssoToken) {
                // Post directly to the meeting chat via backend Graph API endpoint
                const resp = await fetch("/api/sparkinsights/post-to-chat", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${ssoToken}`,
                    },
                    body: JSON.stringify({ chatId, htmlBody }),
                });
                if (!resp.ok) {
                    const err = await resp.json().catch(() => ({}));
                    throw new Error(err.error ?? `Server error ${resp.status}`);
                }
                setTeamsPostDone(true);
            } else {
                // Fallback: copy to clipboard if chatId not available
                const plainText = htmlBody.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ");
                await navigator.clipboard.writeText(plainText);
                setTeamsPostDone(true);
                setActionError("📋 Copied to clipboard — chatId not available so paste manually (Ctrl+V).");
            }
        } catch (e) {
            // Last resort: clipboard
            try {
                await navigator.clipboard.writeText(htmlBody.replace(/<[^>]+>/g, ""));
                setTeamsPostDone(true);
                setActionError(`Direct post failed (${e.message}). Summary copied — paste with Ctrl+V.`);
            } catch (_) {
                setActionError(`Failed to post to Teams: ${e.message}`);
            }
        } finally {
            setTeamsPostLoading(false);
        }
    };

    // ── Create follow-up calendar invite via Teams SDK composeMeeting ────────
    const createCalendarInvite = async () => {
        setCalendarLoading(true);
        setActionError("");
        try {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(10, 0, 0, 0);
            const end = new Date(tomorrow);
            end.setHours(11, 0, 0, 0);

            let content = "Follow-up meeting agenda:\n\n";
            if (insights?.actionItems?.length) {
                content += "Action Items to Review:\n";
                insights.actionItems.forEach(a => {
                    content += `• ${a.action}`;
                    if (a.owner) content += ` (${a.owner})`;
                    content += "\n";
                });
            }
            if (insights?.openQuestions?.length) {
                content += "\nOpen Questions to Resolve:\n";
                insights.openQuestions.forEach(q => { content += `• ${q.question}\n`; });
            }

            // Real attendee emails from Teams participant list
            const attendees = (teamsParticipants ?? [])
                .map(p => p.email || p.userPrincipalName)
                .filter(Boolean);

            // microsoftTeams.calendar.composeMeeting — correct Teams SDK v2 API
            // Opens the Teams new meeting form pre-filled. No "Something went wrong".
            await microsoftTeams.calendar.composeMeeting({
                subject: `Follow-up: ${meetingTitle ?? "Teams Meeting"}`,
                startTime: tomorrow.toISOString(),
                endTime: end.toISOString(),
                content,
                attendees,
            });
            setCalendarDone(true);
        } catch (e) {
            // composeMeeting unavailable in older Teams version — deep link fallback
            try {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(10, 0, 0, 0);
                const end = new Date(tomorrow);
                end.setHours(11, 0, 0, 0);
                const sub = encodeURIComponent(`Follow-up: ${meetingTitle ?? "Teams Meeting"}`);
                await microsoftTeams.app.openLink(
                    `https://teams.microsoft.com/l/meeting/new?subject=${sub}` +
                    `&startTime=${encodeURIComponent(tomorrow.toISOString())}` +
                    `&endTime=${encodeURIComponent(end.toISOString())}`
                );
                setCalendarDone(true);
            } catch (e2) {
                setActionError(`Calendar invite failed: ${e2.message}`);
            }
        } finally {
            setCalendarLoading(false);
        }
    };

    // ── Export transcript + insights to Word doc via backend ─────────────────
    // Backend returns HTML bytes — saved as .doc which Word opens natively.
    const exportToWordDoc = async () => {
        setWordExportLoading(true);
        setActionError("");
        try {
            const resp = await fetch("/api/sparkinsights/export-word", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    transcript: meetingSttResult?.fullTranscript ?? "",
                    utterances: meetingSttResult?.utterances ?? [],
                    insights,
                    meetingTitle: meetingTitle ?? "Teams Meeting",
                    participants: derivedParticipants.map(p => p.label),
                }),
            });
            if (!resp.ok) {
                const errText = await resp.text().catch(() => `HTTP ${resp.status}`);
                throw new Error(errText || `Server error ${resp.status}`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            // .doc extension — Word opens HTML-format .doc files perfectly
            a.download = `${(meetingTitle ?? "Meeting").replace(/[^a-z0-9]/gi, "_")}_Summary.doc`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            setWordExportDone(true);
        } catch (e) {
            setActionError(`Word export failed: ${e.message}`);
        } finally {
            setWordExportLoading(false);
        }
    };

    // ── Upload Word doc to OneDrive ───────────────────────────────────────────
    const uploadToOneDrive = async () => {
        setOneDriveLoading(true);
        setActionError("");
        try {
            // Get Teams SSO token to pass to backend for Graph OBO flow
            let ssoToken = "";
            try {
                ssoToken = await microsoftTeams.authentication.getAuthToken();
            } catch (tokenErr) {
                console.warn("Could not get Teams SSO token:", tokenErr.message);
            }

            const resp = await fetch("/api/sparkinsights/upload-onedrive", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(ssoToken ? { "Authorization": `Bearer ${ssoToken}` } : {}),
                },
                body: JSON.stringify({
                    transcript: meetingSttResult?.fullTranscript ?? "",
                    utterances: meetingSttResult?.utterances ?? [],
                    insights,
                    meetingTitle: meetingTitle ?? "Teams Meeting",
                    participants: derivedParticipants.map(p => p.label),
                }),
            });
            if (resp.status === 401) {
                throw new Error(
                    "OneDrive requires Files.ReadWrite permission. " +
                    "Ask your admin to grant consent for the Spark Anywhere AAD app, " +
                    "then try again."
                );
            }
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.error ?? `Server error ${resp.status}`);
            }
            const data = await resp.json();
            setOneDriveDone(true);
            // Open the uploaded file in OneDrive
            if (data.webUrl) {
                microsoftTeams.app.openLink(data.webUrl).catch(() => {
                    window.open(data.webUrl, "_blank");
                });
            }
        } catch (e) {
            setActionError(`OneDrive upload failed: ${e.message}`);
        } finally {
            setOneDriveLoading(false);
        }
    };

    // ── Tab definitions ───────────────────────────────────────────────────────
    // teamsParticipants: real display names from Teams SDK (passed from ChatPanel)
    // derivedParticipants: utterance-based list, enriched with real names where possible
    const derivedParticipants = React.useMemo(() => {
        const seen = new Set();
        const result = [];
        (meetingSttResult?.utterances ?? []).forEach(u => {
            const label = u.speakerLabel ?? `Speaker ${u.speakerTag ?? "?"}`;
            if (!seen.has(label)) {
                seen.add(label);
                const utterances = meetingSttResult.utterances.filter(x =>
                    (x.speakerLabel ?? `Speaker ${x.speakerTag ?? "?"}`) === label
                );
                const totalWords = utterances.reduce((s, x) =>
                    s + (x.text?.split(" ").length ?? 0), 0);
                const totalSecs = utterances.reduce((s, x) =>
                    s + ((x.endSeconds ?? 0) - (x.startSeconds ?? 0)), 0);
                result.push({ label, utteranceCount: utterances.length, totalWords, totalSecs });
            }
        });
        return result;
    }, [meetingSttResult]);

    const tabs = [
        { id: "utterances", label: "🗣️ Speakers" },
        { id: "participants", label: "👥 Participants" },
        { id: "plain", label: "📄 Transcript" },
        { id: "insights", label: "💡 Insights" },
        { id: "actions", label: "⚡ Actions" },
    ];

    // ── Shared card component ─────────────────────────────────────────────────
    const InsightCard = ({ emoji, title, children, count }) => (
        <div style={{
            marginBottom: 16,
            borderRadius: 10,
            border: `1px solid ${cardBorder}`,
            background: cardBg,
            overflow: "hidden",
        }}>
            <div style={{
                padding: "10px 14px",
                borderBottom: `1px solid ${cardBorder}`,
                display: "flex",
                alignItems: "center",
                gap: 8,
            }}>
                <span style={{ fontSize: 16 }}>{emoji}</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: textColor }}>{title}</span>
                {count !== undefined && (
                    <span style={{
                        marginLeft: "auto",
                        fontSize: 11,
                        background: tagBg,
                        color: textSecondary,
                        borderRadius: 99,
                        padding: "2px 8px",
                        fontWeight: 600,
                    }}>{count}</span>
                )}
            </div>
            <div style={{ padding: "10px 14px" }}>
                {children}
            </div>
        </div>
    );

    const Tag = ({ text, color }) => (
        <span style={{
            display: "inline-block",
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 7px",
            borderRadius: 99,
            background: color ?? tagBg,
            color: color ? "#fff" : textSecondary,
            marginLeft: 6,
        }}>{text}</span>
    );

    const ActionButton = ({ onClick, loading, done, disabled, children, variant = "primary" }) => {
        const bg = variant === "primary" ? primaryIconColor
            : variant === "success" ? successGreen
                : variant === "secondary" ? (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.07)")
                    : primaryIconColor;

        const col = variant === "secondary"
            ? textColor : "#fff";

        return (
            <button
                type="button"
                onClick={onClick}
                disabled={disabled || loading || done}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "9px 16px",
                    borderRadius: 9,
                    border: "none",
                    background: done ? successGreen : bg,
                    color: done ? "#fff" : col,
                    cursor: disabled || loading || done ? "not-allowed" : "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    opacity: disabled ? 0.5 : 1,
                    transition: "opacity 0.2s",
                    width: "100%",
                    justifyContent: "center",
                    marginBottom: 10,
                }}
            >
                {loading ? "⏳" : done ? "✅" : null}
                {loading ? " Working…" : done ? " Done!" : children}
            </button>
        );
    };

    // ── RENDER ────────────────────────────────────────────────────────────────
    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>

            {/* Tab bar */}
            <div style={{
                display: "flex",
                gap: 4,
                padding: "0 16px",
                borderBottom: `1px solid ${cardBorder}`,
                overflowX: "auto",
                flexShrink: 0,
            }}>
                {tabs.map(t => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setActiveTab(t.id)}
                        style={{
                            padding: "9px 12px",
                            border: "none",
                            background: "none",
                            color: activeTab === t.id ? primaryIconColor : textSecondary,
                            fontWeight: activeTab === t.id ? 700 : 400,
                            fontSize: 12,
                            cursor: "pointer",
                            borderBottom: activeTab === t.id ? `2px solid ${primaryIconColor}` : "2px solid transparent",
                            whiteSpace: "nowrap",
                            transition: "color 0.15s",
                        }}
                    >
                        {t.label}
                        {t.id === "insights" && insightsFetched && !insightsLoading && (
                            <span style={{ marginLeft: 5, fontSize: 10, color: successGreen }}>●</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: 16, minHeight: 0 }}>

                {/* ── SPEAKERS TAB (existing utterances view) ────────────────── */}
                {activeTab === "utterances" && (
                    <div>
                        {meetingSttResult?.utterances?.length > 0 ? (
                            meetingSttResult.utterances.map((u, i) => (
                                <div key={i} style={{
                                    marginBottom: 12,
                                    padding: "10px 13px",
                                    borderRadius: 10,
                                    background: cardBg,
                                    border: `1px solid ${cardBorder}`,
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                                        <span style={{ fontWeight: 700, fontSize: 12, color: primaryIconColor }}>
                                            {u.speakerLabel ?? `Speaker ${u.speakerTag}`}
                                        </span>
                                        <span style={{ fontSize: 11, color: textTertiary }}>
                                            {u.startFormatted} – {u.endFormatted}
                                        </span>
                                        {u.confidence != null && (
                                            <span style={{ marginLeft: "auto", fontSize: 11, color: textTertiary }}>
                                                {Math.round(u.confidence * 100)}%
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 13, color: textColor, lineHeight: 1.5 }}>{u.text}</div>
                                </div>
                            ))
                        ) : (
                            <div style={{ color: textSecondary, fontSize: 13 }}>No speaker data available.</div>
                        )}
                    </div>
                )}

                {/* ── WORDS TAB (existing word detail view) ─────────────────── */}
                {activeTab === "participants" && (
                    <div>
                        {derivedParticipants.length === 0 ? (
                            <div style={{ color: textSecondary, fontSize: 13, padding: 8 }}>
                                No speaker data available.
                            </div>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {derivedParticipants.map((p, i) => {
                                    const mins = Math.floor(p.totalSecs / 60);
                                    const secs = String(Math.round(p.totalSecs % 60)).padStart(2, "0");
                                    const pct = meetingSttResult?.utterances?.length
                                        ? Math.round((p.utteranceCount / meetingSttResult.utterances.length) * 100)
                                        : 0;
                                    const avatarColors = ["#6366f1", "#0ea5e9", "#22c55e", "#f59e0b", "#ec4899", "#8b5cf6"];
                                    const avatarColor = avatarColors[i % avatarColors.length];
                                    return (
                                        <div key={p.label} style={{
                                            padding: "12px 14px",
                                            borderRadius: 10,
                                            background: cardBg,
                                            border: `1px solid ${cardBorder}`,
                                        }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                                {/* Avatar */}
                                                <div style={{
                                                    width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                                                    background: avatarColor,
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    fontSize: 14, fontWeight: 700, color: "#fff",
                                                }}>
                                                    {p.label.charAt(0).toUpperCase()}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 700, fontSize: 13, color: textColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {p.label}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                                                        {p.utteranceCount} segments · {p.totalWords} words · {mins}:{secs} speaking
                                                    </div>
                                                </div>
                                                {/* Share % badge */}
                                                <div style={{
                                                    fontSize: 13, fontWeight: 800,
                                                    color: avatarColor, flexShrink: 0,
                                                }}>
                                                    {pct}%
                                                </div>
                                            </div>
                                            {/* Speaking share bar */}
                                            <div style={{ height: 5, borderRadius: 99, background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)" }}>
                                                <div style={{
                                                    height: "100%", borderRadius: 99,
                                                    background: avatarColor,
                                                    width: `${pct}%`,
                                                    transition: "width 0.6s ease",
                                                }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* ── PLAIN TRANSCRIPT TAB (existing) ───────────────────────── */}
                {activeTab === "plain" && (
                    <div style={{
                        fontSize: 13,
                        color: textColor,
                        lineHeight: 1.7,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                    }}>
                        {meetingSttResult?.fullTranscript ?? "No transcript available."}
                    </div>
                )}

                {/* ── INSIGHTS TAB ───────────────────────────────────────────── */}
                {activeTab === "insights" && (
                    <div>
                        {/* Loading state */}
                        {insightsLoading && (
                            <div style={{ textAlign: "center", padding: 40, color: textSecondary }}>
                                <div style={{ fontSize: 28, marginBottom: 12 }}>✨</div>
                                <div style={{ fontWeight: 600, fontSize: 14, color: textColor, marginBottom: 6 }}>
                                    Spark is analysing your meeting…
                                </div>
                                <div style={{ fontSize: 12 }}>Extracting key points, actions, and decisions</div>
                            </div>
                        )}

                        {/* Error state */}
                        {insightsError && !insightsLoading && (
                            <div style={{
                                padding: "12px 14px",
                                borderRadius: 10,
                                background: "rgba(211,47,47,0.1)",
                                border: "1px solid rgba(211,47,47,0.3)",
                                color: "#c62828",
                                fontSize: 13,
                                marginBottom: 12,
                            }}>
                                ⚠️ {insightsError}
                                <button
                                    type="button"
                                    onClick={() => { setInsightsFetched(false); generateInsights(); }}
                                    style={{
                                        marginLeft: 10, fontSize: 12, color: primaryIconColor,
                                        background: "none", border: "none", cursor: "pointer", fontWeight: 600,
                                    }}
                                >
                                    Retry
                                </button>
                            </div>
                        )}

                        {/* Insights content */}
                        {insights && !insightsLoading && (
                            <>
                                {/* Overall summary */}
                                {insights.overallSummary && (
                                    <div style={{
                                        padding: "12px 14px",
                                        borderRadius: 10,
                                        background: isDark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.08)",
                                        border: `1px solid ${isDark ? "rgba(99,102,241,0.3)" : "rgba(99,102,241,0.2)"}`,
                                        marginBottom: 16,
                                        fontSize: 13,
                                        color: textColor,
                                        lineHeight: 1.6,
                                    }}>
                                        <div style={{ fontWeight: 700, fontSize: 11, color: "#6366f1", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                            ✨ Meeting Summary
                                        </div>
                                        {insights.overallSummary}
                                    </div>
                                )}

                                {/* Key Points */}
                                {insights.keyPoints?.length > 0 && (
                                    <InsightCard emoji="🔑" title="Key Points" count={insights.keyPoints.length}>
                                        {insights.keyPoints.map((k, i) => (
                                            <div key={i} style={{
                                                display: "flex", gap: 8, alignItems: "flex-start",
                                                paddingBottom: i < insights.keyPoints.length - 1 ? 8 : 0,
                                                marginBottom: i < insights.keyPoints.length - 1 ? 8 : 0,
                                                borderBottom: i < insights.keyPoints.length - 1 ? `1px solid ${cardBorder}` : "none",
                                            }}>
                                                <span style={{ color: primaryIconColor, marginTop: 2, flexShrink: 0 }}>•</span>
                                                <div>
                                                    <span style={{ fontSize: 13, color: textColor }}>{k.point}</span>
                                                    {k.speaker && <Tag text={k.speaker} />}
                                                </div>
                                            </div>
                                        ))}
                                    </InsightCard>
                                )}

                                {/* Takeaways */}
                                {insights.takeaways?.length > 0 && (
                                    <InsightCard emoji="💡" title="Takeaways" count={insights.takeaways.length}>
                                        {insights.takeaways.map((t, i) => (
                                            <div key={i} style={{
                                                display: "flex", gap: 8, paddingBottom: i < insights.takeaways.length - 1 ? 8 : 0,
                                                marginBottom: i < insights.takeaways.length - 1 ? 8 : 0,
                                                borderBottom: i < insights.takeaways.length - 1 ? `1px solid ${cardBorder}` : "none",
                                            }}>
                                                <span style={{ color: "#f59e0b", flexShrink: 0 }}>💡</span>
                                                <span style={{ fontSize: 13, color: textColor }}>{t.takeaway ?? t.text}</span>
                                            </div>
                                        ))}
                                    </InsightCard>
                                )}

                                {/* Action Items */}
                                {insights.actionItems?.length > 0 && (
                                    <InsightCard emoji="✅" title="Action Items" count={insights.actionItems.length}>
                                        {insights.actionItems.map((a, i) => (
                                            <div key={i} style={{
                                                paddingBottom: i < insights.actionItems.length - 1 ? 10 : 0,
                                                marginBottom: i < insights.actionItems.length - 1 ? 10 : 0,
                                                borderBottom: i < insights.actionItems.length - 1 ? `1px solid ${cardBorder}` : "none",
                                            }}>
                                                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                                                    <span style={{ color: successGreen, flexShrink: 0, marginTop: 1 }}>☐</span>
                                                    <span style={{ fontSize: 13, color: textColor, flex: 1 }}>{a.action}</span>
                                                </div>
                                                <div style={{ display: "flex", gap: 6, marginTop: 5, marginLeft: 20 }}>
                                                    {a.owner && <Tag text={`👤 ${a.owner}`} color="#1976d2" />}
                                                    {a.deadline && <Tag text={`📅 ${a.deadline}`} color={warningAmber} />}
                                                </div>
                                            </div>
                                        ))}
                                    </InsightCard>
                                )}

                                {/* Decisions */}
                                {insights.decisions?.length > 0 && (
                                    <InsightCard emoji="🎯" title="Decisions Made" count={insights.decisions.length}>
                                        {insights.decisions.map((d, i) => (
                                            <div key={i} style={{
                                                display: "flex", gap: 8, alignItems: "flex-start",
                                                paddingBottom: i < insights.decisions.length - 1 ? 8 : 0,
                                                marginBottom: i < insights.decisions.length - 1 ? 8 : 0,
                                                borderBottom: i < insights.decisions.length - 1 ? `1px solid ${cardBorder}` : "none",
                                            }}>
                                                <span style={{ color: "#7c3aed", flexShrink: 0 }}>🎯</span>
                                                <div>
                                                    <span style={{ fontSize: 13, color: textColor }}>{d.decision ?? d.decision_}</span>
                                                    {d.madeBy && <Tag text={d.madeBy} color="#7c3aed" />}
                                                </div>
                                            </div>
                                        ))}
                                    </InsightCard>
                                )}

                                {/* Open Questions */}
                                {insights.openQuestions?.length > 0 && (
                                    <InsightCard emoji="❓" title="Open Questions" count={insights.openQuestions.length}>
                                        {insights.openQuestions.map((q, i) => (
                                            <div key={i} style={{
                                                display: "flex", gap: 8, alignItems: "flex-start",
                                                paddingBottom: i < insights.openQuestions.length - 1 ? 8 : 0,
                                                marginBottom: i < insights.openQuestions.length - 1 ? 8 : 0,
                                                borderBottom: i < insights.openQuestions.length - 1 ? `1px solid ${cardBorder}` : "none",
                                            }}>
                                                <span style={{ color: "#d97706", flexShrink: 0 }}>❓</span>
                                                <div>
                                                    <span style={{ fontSize: 13, color: textColor }}>{q.question}</span>
                                                    {q.raisedBy && <Tag text={q.raisedBy} />}
                                                </div>
                                            </div>
                                        ))}
                                    </InsightCard>
                                )}

                                {/* Sentiment Analysis */}
                                {insights.sentiment && (
                                    <InsightCard emoji="😊" title="Sentiment Analysis">
                                        {/* Overall sentiment meter */}
                                        <div style={{ marginBottom: 14 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                                                <span style={{
                                                    fontSize: 28,
                                                    lineHeight: 1,
                                                }}>
                                                    {insights.sentiment.overall === "positive" ? "😊"
                                                        : insights.sentiment.overall === "negative" ? "😟" : "😐"}
                                                </span>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: 13, color: textColor, textTransform: "capitalize" }}>
                                                        {insights.sentiment.overall} Meeting
                                                    </div>
                                                    <div style={{ fontSize: 11, color: textSecondary }}>
                                                        Score: {insights.sentiment.score > 0 ? "+" : ""}{insights.sentiment.score.toFixed(2)} / 1.00
                                                    </div>
                                                </div>
                                                {/* Score bar */}
                                                <div style={{ flex: 1, height: 8, borderRadius: 99, background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)", overflow: "hidden" }}>
                                                    <div style={{
                                                        height: "100%",
                                                        width: `${Math.round(((insights.sentiment.score + 1) / 2) * 100)}%`,
                                                        borderRadius: 99,
                                                        background: insights.sentiment.overall === "positive" ? "#22c55e"
                                                            : insights.sentiment.overall === "negative" ? "#ef4444" : "#f59e0b",
                                                        transition: "width 0.6s ease",
                                                    }} />
                                                </div>
                                            </div>
                                            {insights.sentiment.summary && (
                                                <div style={{ fontSize: 12, color: textSecondary, lineHeight: 1.5, fontStyle: "italic" }}>
                                                    {insights.sentiment.summary}
                                                </div>
                                            )}
                                        </div>

                                        {/* Per-speaker sentiment */}
                                        {insights.sentiment.speakerSentiments?.length > 0 && (
                                            <div style={{ marginBottom: 12 }}>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                                    By Speaker
                                                </div>
                                                {insights.sentiment.speakerSentiments.map((s, i) => (
                                                    <div key={i} style={{
                                                        display: "flex", alignItems: "center", gap: 8,
                                                        padding: "5px 0",
                                                        borderBottom: i < insights.sentiment.speakerSentiments.length - 1 ? `1px solid ${cardBorder}` : "none",
                                                    }}>
                                                        <span style={{ fontSize: 14 }}>
                                                            {s.sentiment === "positive" ? "😊" : s.sentiment === "negative" ? "😟" : "😐"}
                                                        </span>
                                                        <span style={{ fontSize: 12, color: textColor, fontWeight: 600, flex: 1 }}>{s.speaker}</span>
                                                        <span style={{
                                                            fontSize: 11, fontWeight: 700, textTransform: "capitalize",
                                                            color: s.sentiment === "positive" ? "#22c55e" : s.sentiment === "negative" ? "#ef4444" : "#f59e0b",
                                                        }}>{s.sentiment}</span>
                                                        {s.note && (
                                                            <span style={{ fontSize: 11, color: textSecondary, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.note}>
                                                                — {s.note}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Highlights */}
                                        {insights.sentiment.highlights?.length > 0 && (
                                            <div>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                                    Notable Moments
                                                </div>
                                                {insights.sentiment.highlights.map((h, i) => (
                                                    <div key={i} style={{
                                                        display: "flex", gap: 8, alignItems: "flex-start",
                                                        marginBottom: 6,
                                                    }}>
                                                        <span style={{ fontSize: 13, flexShrink: 0 }}>
                                                            {h.type === "positive" ? "✨" : h.type === "tension" ? "⚡" : "⚠️"}
                                                        </span>
                                                        <span style={{ fontSize: 12, color: textColor, lineHeight: 1.5 }}>{h.text}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </InsightCard>
                                )}

                                {/* Regenerate button */}
                                <button
                                    type="button"
                                    onClick={() => { setInsightsFetched(false); setInsights(null); generateInsights(); }}
                                    style={{
                                        width: "100%", padding: "8px 0", marginTop: 4,
                                        background: "none", border: `1px solid ${cardBorder}`,
                                        borderRadius: 9, color: textSecondary, fontSize: 12,
                                        cursor: "pointer", fontWeight: 600,
                                    }}
                                >
                                    🔄 Regenerate Insights
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* ── ACTIONS TAB ────────────────────────────────────────────── */}
                {activeTab === "actions" && (
                    <div>
                        {!insightsFetched && (
                            <div style={{
                                padding: "12px 14px", borderRadius: 10, marginBottom: 16,
                                background: isDark ? "rgba(255,193,7,0.1)" : "rgba(255,193,7,0.08)",
                                border: "1px solid rgba(255,193,7,0.3)",
                                fontSize: 13, color: textColor,
                            }}>
                                💡 Visit the <strong>Insights</strong> tab first to generate meeting insights — Actions use them to draft your email and calendar invite.
                            </div>
                        )}

                        {actionError && (
                            <div style={{
                                padding: "10px 14px", borderRadius: 10, marginBottom: 12,
                                background: "rgba(211,47,47,0.1)", border: "1px solid rgba(211,47,47,0.3)",
                                color: "#c62828", fontSize: 13,
                            }}>
                                ⚠️ {actionError}
                            </div>
                        )}

                        {/* ── Email action ────────────────────────────────── */}
                        <InsightCard emoji="📧" title="Send Follow-up Email">
                            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 12, lineHeight: 1.5 }}>
                                Spark will draft a professional follow-up email with the meeting summary, decisions, and action items for all participants.
                            </div>

                            {!emailDraftVisible ? (
                                <ActionButton
                                    onClick={generateEmailDraft}
                                    loading={emailLoading}
                                    disabled={!meetingSttResult?.fullTranscript}
                                >
                                    ✉️ Generate Email Draft
                                </ActionButton>
                            ) : (
                                <>
                                    {/* Draft preview */}
                                    <div style={{
                                        padding: "10px 12px", borderRadius: 9,
                                        background: cardBg, border: `1px solid ${cardBorder}`,
                                        marginBottom: 10,
                                    }}>
                                        {/* To field — real participant emails */}
                                        {(teamsParticipants?.length > 0) && (
                                            <>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: textSecondary, marginBottom: 4, textTransform: "uppercase" }}>To</div>
                                                <div style={{ fontSize: 12, color: textColor, marginBottom: 10 }}>
                                                    {teamsParticipants.map(p => `${p.displayName} <${p.email || p.userPrincipalName}>`).join(", ")}
                                                </div>
                                            </>
                                        )}
                                        <div style={{ fontSize: 11, fontWeight: 700, color: textSecondary, marginBottom: 4, textTransform: "uppercase" }}>Subject</div>
                                        <div style={{ fontSize: 13, color: emailDraft?.subject ? textColor : textSecondary, marginBottom: 10, fontWeight: 600, fontStyle: emailDraft?.subject ? "normal" : "italic" }}>
                                            {emailDraft?.subject || "(no subject returned — try regenerating)"}
                                        </div>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: textSecondary, marginBottom: 4, textTransform: "uppercase" }}>Preview</div>
                                        <div style={{
                                            fontSize: 12, color: emailDraft?.body ? textSecondary : textSecondary, lineHeight: 1.6,
                                            maxHeight: 120, overflowY: "auto",
                                            whiteSpace: "pre-wrap", fontStyle: emailDraft?.body ? "normal" : "italic",
                                        }}>
                                            {emailDraft?.body
                                                ? (emailDraft.body.slice(0, 300) + (emailDraft.body.length > 300 ? "…" : ""))
                                                : "(empty body — insights may not have loaded. Visit Insights tab first, then regenerate.)"}
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", gap: 8 }}>
                                        <button
                                            type="button"
                                            onClick={openEmailClient}
                                            style={{
                                                flex: 1, padding: "8px 0", borderRadius: 9,
                                                background: primaryIconColor, color: "#fff",
                                                border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                                            }}
                                        >
                                            📨 Open in Mail
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setEmailDraftVisible(false); setEmailDraft(null); }}
                                            style={{
                                                flex: 1, padding: "8px 0", borderRadius: 9,
                                                background: "none", border: `1px solid ${cardBorder}`,
                                                color: textSecondary, cursor: "pointer", fontSize: 13, fontWeight: 600,
                                            }}
                                        >
                                            🔄 Regenerate
                                        </button>
                                    </div>
                                </>
                            )}
                        </InsightCard>

                        {/* ── Calendar invite ─────────────────────────────── */}
                        <InsightCard emoji="📅" title="Create Follow-up Calendar Invite">
                            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 12, lineHeight: 1.5 }}>
                                Creates a follow-up meeting invite pre-filled with an agenda based on open action items and unresolved questions. Opens in Teams calendar.
                            </div>
                            <ActionButton
                                onClick={createCalendarInvite}
                                loading={calendarLoading}
                                done={calendarDone}
                                disabled={!meetingSttResult?.fullTranscript}
                            >
                                📅 Create Follow-up Meeting
                            </ActionButton>
                            {calendarDone && (
                                <div style={{ fontSize: 12, color: successGreen, textAlign: "center" }}>
                                    Teams calendar opened — review and send the invite.
                                </div>
                            )}
                        </InsightCard>

                        {/* ── Post to Teams ────────────────────────────────── */}
                        <InsightCard emoji="💬" title="Post Summary to Meeting Chat">
                            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 12, lineHeight: 1.5 }}>
                                Posts the meeting summary, decisions, and action items directly to the Teams meeting chat. Falls back to clipboard if not available.
                            </div>
                            <ActionButton
                                onClick={postToTeamsChat}
                                loading={teamsPostLoading}
                                done={teamsPostDone}
                                disabled={!meetingSttResult?.fullTranscript}
                            >
                                📋 Copy Summary to Clipboard
                            </ActionButton>
                            {teamsPostDone && (
                                <div style={{ fontSize: 12, color: successGreen, textAlign: "center" }}>
                                    ✅ Posted to meeting chat.
                                </div>
                            )}
                        </InsightCard>
                        {/* ── Export to Word ──────────────────────────────── */}
                        <InsightCard emoji="📄" title="Export to Word Document">
                            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 12, lineHeight: 1.5 }}>
                                Exports the full transcript, speaker breakdown, all insights, and sentiment analysis into a professionally formatted Word (.docx) file.
                            </div>
                            <ActionButton
                                onClick={exportToWordDoc}
                                loading={wordExportLoading}
                                done={wordExportDone}
                                disabled={!meetingSttResult?.fullTranscript}
                            >
                                📄 Download Word Doc
                            </ActionButton>
                            {wordExportDone && (
                                <button
                                    type="button"
                                    onClick={() => setWordExportDone(false)}
                                    style={{ width: "100%", padding: "6px 0", background: "none", border: `1px solid ${cardBorder}`, borderRadius: 9, color: textSecondary, fontSize: 12, cursor: "pointer" }}
                                >
                                    ↓ Download Again
                                </button>
                            )}
                        </InsightCard>

                        {/* ── Upload to OneDrive ───────────────────────────── */}
                        <InsightCard emoji="☁️" title="Upload to OneDrive">
                            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 12, lineHeight: 1.5 }}>
                                Generates the Word document and uploads it directly to your OneDrive. Opens the file in browser when done. Requires OneDrive access via your Teams account.
                            </div>
                            <ActionButton
                                onClick={uploadToOneDrive}
                                loading={oneDriveLoading}
                                done={oneDriveDone}
                                disabled={!meetingSttResult?.fullTranscript}
                            >
                                ☁️ Upload to OneDrive
                            </ActionButton>
                            {oneDriveDone && (
                                <div style={{ fontSize: 12, color: successGreen, textAlign: "center" }}>
                                    Uploaded to OneDrive — file opened in browser.
                                </div>
                            )}
                        </InsightCard>

                    </div>
                )}
            </div>
        </div>
    );
};

export default SttTabViewer;